import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "../../components/ui/Icon.js";
import { loginRequest } from "../../lib/auth.js";

interface NavState {
  from?: string;
}

export function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const from = (location.state as NavState | null)?.from ?? "/";

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const res = await loginRequest(email, password);
    setSubmitting(false);
    if (res.ok) {
      navigate(from, { replace: true });
    } else {
      setError(res.error === "invalid_credentials" ? "Invalid email or password." : res.error);
    }
  }

  return (
    <div
      style={{
        width: "100%",
        height: "100vh",
        display: "flex",
        overflow: "hidden",
        background: "var(--bg)",
      }}
    >
      <div
        style={{
          flex: 1,
          position: "relative",
          background:
            "radial-gradient(ellipse at 30% 40%, rgba(59, 130, 246, 0.15) 0%, transparent 50%), radial-gradient(ellipse at 70% 70%, rgba(168, 85, 247, 0.12) 0%, transparent 50%), var(--bg)",
          borderRight: "1px solid var(--border)",
          padding: 48,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div className="se-logo-mark" style={{ width: 28, height: 28 }}>
            <span style={{ fontSize: 15 }}>S</span>
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: "-0.02em" }}>
            SpotEdge
          </div>
        </div>
        <div style={{ marginTop: "auto", maxWidth: 460 }}>
          <span className="se-pill se-pill-ai" style={{ marginBottom: 18, display: "inline-flex" }}>
            <Icon name="sparkle" size={10} /> Self-learning model · v0.1
          </span>
          <h1
            style={{
              fontSize: 36,
              lineHeight: 1.15,
              fontWeight: 600,
              letterSpacing: "-0.025em",
              margin: 0,
              marginTop: 14,
            }}
          >
            Institutional-grade spot trading intelligence.
          </h1>
          <p className="muted" style={{ fontSize: 14, lineHeight: 1.55, marginTop: 16, maxWidth: 420 }}>
            AI-ranked signals across 8 exchanges, calibrated probabilities, and
            walk-forward validation on every trade. Spot only — no margin, no
            leverage.
          </p>
        </div>
        <div className="muted" style={{ fontSize: 11, marginTop: 32 }}>
          © 2026 SpotEdge — spot trading only.
        </div>
      </div>

      <div
        style={{
          width: 460,
          padding: "64px 56px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <h2 style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em", margin: 0 }}>
          Sign in
        </h2>
        <p className="muted" style={{ fontSize: 13, marginTop: 6, marginBottom: 28 }}>
          Welcome back. Enter your credentials below.
        </p>

        <form onSubmit={onSubmit}>
          <label
            style={{ fontSize: 11, color: "var(--text-mid)", marginBottom: 4, display: "block" }}
          >
            Email
          </label>
          <div style={{ position: "relative", marginBottom: 14 }}>
            <Icon
              name="mail"
              size={14}
              style={{ position: "absolute", left: 10, top: 11, color: "var(--text-faint)" }}
            />
            <input
              className="se-input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ paddingLeft: 32 }}
            />
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <label style={{ fontSize: 11, color: "var(--text-mid)" }}>Password</label>
          </div>
          <div style={{ position: "relative", marginBottom: 14 }}>
            <Icon
              name="lock"
              size={14}
              style={{ position: "absolute", left: 10, top: 11, color: "var(--text-faint)" }}
            />
            <input
              className="se-input"
              type={showPw ? "text" : "password"}
              autoComplete="current-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ paddingLeft: 32, paddingRight: 32 }}
            />
            <button
              type="button"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? "Hide password" : "Show password"}
              style={{
                position: "absolute",
                right: 10,
                top: 11,
                color: "var(--text-faint)",
                background: "transparent",
                border: 0,
                cursor: "pointer",
              }}
            >
              <Icon name="eye" size={14} />
            </button>
          </div>

          {error ? (
            <div
              style={{
                marginBottom: 12,
                padding: "8px 10px",
                background: "var(--sell-soft)",
                color: "var(--sell)",
                borderRadius: 6,
                fontSize: 12,
              }}
            >
              {error}
            </div>
          ) : null}

          <button
            type="submit"
            disabled={submitting}
            className="se-btn se-btn-primary"
            style={{ width: "100%", padding: "11px 0", fontWeight: 600 }}
          >
            {submitting ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="muted" style={{ fontSize: 12, marginTop: 24, textAlign: "center" }}>
          New to SpotEdge?{" "}
          <Link to="/signup" style={{ color: "var(--primary)" }}>
            Create account
          </Link>
        </div>
      </div>
    </div>
  );
}
